import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { oltAPI } from '../lib/api';
import { loadSettings, subscribeSettings, playAlarmSound } from '../lib/alarmSounds';

/**
 * Watcher específico de OLT online/offline.
 * Faz poll a cada 10 segundos e emite alerta (toast + som `olt_offline`)
 * assim que uma OLT muda para offline. Também notifica quando volta a ficar online.
 *
 * Corre em qualquer tela, enquanto o usuário estiver autenticado.
 */
// Poll a cada 10 s só enquanto a aba está visível. Em background o tick é
// pulado sem gerar request (em tenant grande a resposta de /olts não é trivial
// e ninguém está vendo os toasts); ao voltar para o primeiro plano dispara um
// tick imediato (achado [5.06]).
const OLT_POLL_MS = 10000;

export default function useOltStatusWatcher(enabled) {
  const statusRef = useRef({});      // { oltId: 'online'|'offline' }
  const primedRef = useRef(false);
  const settingsRef = useRef(loadSettings());

  useEffect(() => {
    const unsub = subscribeSettings((next) => { settingsRef.current = next; });
    return unsub;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await oltAPI.list();
        if (cancelled) return;
        const data = res?.data;
        const olts = Array.isArray(data) ? data : (data?.olts || data?.data || []);
        const nextMap = {};
        olts.forEach((o) => { nextMap[o.id] = String(o.status || '').toLowerCase() || 'unknown'; });

        // Primeira passada: só grava o baseline (evita alertar tudo no login)
        if (!primedRef.current) {
          statusRef.current = nextMap;
          primedRef.current = true;
          return;
        }

        const s = settingsRef.current;
        const soundOn = s?.globalEnabled && s?.globalWatcher;

        // Compara com estado anterior
        olts.forEach((o) => {
          const prev = statusRef.current[o.id];
          const now = nextMap[o.id];
          if (!prev || prev === now) return;
          if (now === 'offline' && prev !== 'offline') {
            const lbl = `${o.vendor || 'OLT'} — ${o.name}`;
            toast.error(`OLT offline: ${lbl}`, {
              description: 'Backend não conseguiu contato. Verifique link/energia/CPU.',
              duration: 10000,
            });
            if (soundOn) playAlarmSound('olt_offline');
          } else if (now === 'online' && prev === 'offline') {
            const lbl = `${o.vendor || 'OLT'} — ${o.name}`;
            toast.success(`OLT recuperada: ${lbl}`, { duration: 6000 });
          }
        });
        statusRef.current = nextMap;
      } catch (e) {
        // silencioso — backend externo pode dropar por 60-90s
      }
    };

    tick();
    const id = setInterval(tick, OLT_POLL_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
